import React, { useState, useEffect } from 'react';
import { AlertCircle, Check, Plus, Minus, X, Shield, Search, ChevronLeft, ChevronRight, Save, Users, Radio, Wrench, Mic } from 'lucide-react';
import { useAuth } from '../auth/KeycloakContext';

interface Permission {
  id: string;
  name: string;
  description: string;
}

interface UserroleTemplate {
  id: string;
  name: string;
  description: string | null;
  can_delete: boolean;
  permissions: Permission[];
}

interface ChannelTemplate {
  id: string;
  name: string;
  description: string | null;
  auto_create: boolean;
}

interface ChannelRoleTemplate {
  id: string;
  name: string;
  description: string | null;
  role_type: string;
  speech_priority: string;
  can_mute: boolean;
  can_unmute: boolean;
  can_ban: boolean;
  auto_create: boolean;
}

interface ServiceGroupTemplate {
  id: string;
  name: string;
  description: string | null;
  auto_create: boolean;
}

type TabType = 'userroles' | 'channels' | 'channelroles' | 'servicegroups';

export default function TemplatesPage() {
  const { keycloak } = useAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('userroles');

  // Userrole template state
  const [userroleTemplates, setUserroleTemplates] = useState<UserroleTemplate[]>([]);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loadingUserroles, setLoadingUserroles] = useState(false);
  const [userroleMessage, setUserroleMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState<UserroleTemplate | null>(null);
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());

  // Userrole list state (selection, search, pagination)
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set());
  const [roleSearchQuery, setRoleSearchQuery] = useState('');
  const [roleCurrentPage, setRoleCurrentPage] = useState(1);
  const [rolePageSize, setRolePageSize] = useState(20);
  const [deletingRoles, setDeletingRoles] = useState(false);

  // Channel template state
  const [channelTemplates, setChannelTemplates] = useState<ChannelTemplate[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [channelMessage, setChannelMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState<ChannelTemplate | null>(null);
  const [channelName, setChannelName] = useState('');
  const [channelDescription, setChannelDescription] = useState('');
  const [channelAutoCreate, setChannelAutoCreate] = useState(true);

  // Channel list state (selection, search, pagination)
  const [selectedChannelIds, setSelectedChannelIds] = useState<Set<string>>(new Set());
  const [channelSearchQuery, setChannelSearchQuery] = useState('');
  const [channelCurrentPage, setChannelCurrentPage] = useState(1);
  const [channelPageSize, setChannelPageSize] = useState(20);
  const [deletingChannels, setDeletingChannels] = useState(false);

  // Channel role template state
  const [crTemplates, setCrTemplates] = useState<ChannelRoleTemplate[]>([]);
  const [loadingCr, setLoadingCr] = useState(false);
  const [crMessage, setCrMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCrModal, setShowCrModal] = useState(false);
  const [editingCr, setEditingCr] = useState<ChannelRoleTemplate | null>(null);
  const [crName, setCrName] = useState('');
  const [crDescription, setCrDescription] = useState('');
  const [crAutoCreate, setCrAutoCreate] = useState(true);
  const [crRoleType, setCrRoleType] = useState<'speak' | 'listen_only'>('speak');
  const [crSpeechPriority, setCrSpeechPriority] = useState<'normal' | 'high'>('normal');
  const [crCanMute, setCrCanMute] = useState(false);
  const [crCanUnmute, setCrCanUnmute] = useState(false);
  const [crCanBan, setCrCanBan] = useState(false);

  // Channel role list state (selection, search, pagination)
  const [selectedCrIds, setSelectedCrIds] = useState<Set<string>>(new Set());
  const [crSearchQuery, setCrSearchQuery] = useState('');
  const [crCurrentPage, setCrCurrentPage] = useState(1);
  const [crPageSize, setCrPageSize] = useState(20);
  const [deletingCr, setDeletingCr] = useState(false);

  // Service group template state
  const [sgTemplates, setSgTemplates] = useState<ServiceGroupTemplate[]>([]);
  const [loadingSg, setLoadingSg] = useState(false);
  const [sgMessage, setSgMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showSgModal, setShowSgModal] = useState(false);
  const [editingSg, setEditingSg] = useState<ServiceGroupTemplate | null>(null);
  const [sgName, setSgName] = useState('');
  const [sgDescription, setSgDescription] = useState('');
  const [sgAutoCreate, setSgAutoCreate] = useState(true);

  // Service group list state (selection, search, pagination)
  const [selectedSgIds, setSelectedSgIds] = useState<Set<string>>(new Set());
  const [sgSearchQuery, setSgSearchQuery] = useState('');
  const [sgCurrentPage, setSgCurrentPage] = useState(1);
  const [sgPageSize, setSgPageSize] = useState(20);
  const [deletingSg, setDeletingSg] = useState(false);

  // Load data on mount
  useEffect(() => {
    if (keycloak?.token) {
      loadUserroleTemplates();
      loadPermissions();
      loadChannelTemplates();
      loadCrTemplates();
      loadSgTemplates();
    }
  }, [keycloak?.token]);

  // ==================== Userrole Template Functions ====================

  const loadUserroleTemplates = async () => {
    setLoadingUserroles(true);
    try {
      const response = await fetch('/api/admin/userrole-templates', {
        headers: {
          'Authorization': `Bearer ${keycloak?.token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUserroleTemplates(data);
      } else {
        setUserroleMessage({ type: 'error', text: 'Fehler beim Laden der Rollen-Vorlagen' });
      }
    } catch (error) {
      setUserroleMessage({ type: 'error', text: 'Netzwerkfehler' });
    } finally {
      setLoadingUserroles(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await fetch('/api/admin/permissions', {
        headers: {
          'Authorization': `Bearer ${keycloak?.token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAllPermissions(data);
      }
    } catch (error) {
      console.error('Failed to load permissions', error);
    }
  };

  const openCreateRoleModal = () => {
    setEditingRole(null);
    setRoleName('');
    setRoleDescription('');
    setSelectedPermissions(new Set());
    setShowRoleModal(true);
  };

  const openEditRoleModal = (role: UserroleTemplate) => {
    setEditingRole(role);
    setRoleName(role.name);
    setRoleDescription(role.description || '');
    setSelectedPermissions(new Set(role.permissions.map(p => p.id)));
    setShowRoleModal(true);
  };

  const closeRoleModal = () => {
    setShowRoleModal(false);
    setEditingRole(null);
  };

  const togglePermission = (permId: string) => {
    const newSet = new Set(selectedPermissions);
    if (newSet.has(permId)) {
      newSet.delete(permId);
    } else {
      newSet.add(permId);
    }
    setSelectedPermissions(newSet);
  };

  const saveRole = async () => {
    setUserroleMessage(null);
    try {
      if (editingRole) {
        const updateResponse = await fetch(`/api/admin/userrole-templates/${editingRole.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${keycloak?.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: roleName,
            description: roleDescription || null,
          }),
        });

        if (!updateResponse.ok) {
          const error = await updateResponse.json();
          setUserroleMessage({ type: 'error', text: error.detail || 'Fehler beim Aktualisieren' });
          return;
        }

        await fetch(`/api/admin/userrole-templates/${editingRole.id}/permissions`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${keycloak?.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            permission_ids: Array.from(selectedPermissions),
          }),
        });

        setUserroleMessage({ type: 'success', text: 'Rollen-Vorlage aktualisiert' });
      } else {
        const createResponse = await fetch('/api/admin/userrole-templates', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${keycloak?.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: roleName,
            description: roleDescription || null,
            permission_ids: Array.from(selectedPermissions),
          }),
        });

        if (!createResponse.ok) {
          const error = await createResponse.json();
          setUserroleMessage({ type: 'error', text: error.detail || 'Fehler beim Erstellen' });
          return;
        }

        setUserroleMessage({ type: 'success', text: 'Rollen-Vorlage erstellt' });
      }

      closeRoleModal();
      loadUserroleTemplates();
    } catch (error) {
      setUserroleMessage({ type: 'error', text: 'Netzwerkfehler beim Speichern' });
    }
  };

  const deleteRole = async (roleId: string) => {
    if (!confirm('Möchten Sie diese Rollen-Vorlage wirklich löschen?')) return;

    try {
      const response = await fetch(`/api/admin/userrole-templates/${roleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${keycloak?.token}`,
        },
      });

      if (response.ok) {
        setUserroleMessage({ type: 'success', text: 'Rollen-Vorlage gelöscht' });
        loadUserroleTemplates();
      } else {
        const error = await response.json();
        setUserroleMessage({ type: 'error', text: error.detail || 'Fehler beim Löschen' });
      }
    } catch (error) {
      setUserroleMessage({ type: 'error', text: 'Netzwerkfehler' });
    }
  };

  // Userrole filter & pagination
  const filteredRoles = userroleTemplates.filter((role) => {
    const searchLower = roleSearchQuery.toLowerCase();
    return (
      role.name.toLowerCase().includes(searchLower) ||
      (role.description || '').toLowerCase().includes(searchLower)
    );
  });

  const totalRoles = filteredRoles.length;
  const totalRolePages = Math.ceil(totalRoles / rolePageSize);
  const roleStartIndex = (roleCurrentPage - 1) * rolePageSize;
  const roleEndIndex = roleStartIndex + rolePageSize;
  const currentRoles = filteredRoles.slice(roleStartIndex, roleEndIndex);

  const handleRolePageSizeChange = (newSize: number) => {
    setRolePageSize(newSize);
    setRoleCurrentPage(1);
  };

  const handleRoleSearchChange = (value: string) => {
    setRoleSearchQuery(value);
    setRoleCurrentPage(1);
  };

  const handleSelectAllRoles = (checked: boolean) => {
    if (checked) {
      setSelectedRoleIds(new Set(currentRoles.filter(r => r.can_delete).map(r => r.id)));
    } else {
      setSelectedRoleIds(new Set());
    }
  };

  const handleSelectRole = (roleId: string, checked: boolean) => {
    const newSet = new Set(selectedRoleIds);
    if (checked) {
      newSet.add(roleId);
    } else {
      newSet.delete(roleId);
    }
    setSelectedRoleIds(newSet);
  };

  const handleDeleteSelectedRoles = async () => {
    if (selectedRoleIds.size === 0) return;

    const undeletableRoles = userroleTemplates.filter(r => selectedRoleIds.has(r.id) && !r.can_delete);
    if (undeletableRoles.length > 0) {
      alert(`Folgende Rollen können nicht gelöscht werden: ${undeletableRoles.map(r => r.name).join(', ')}`);
      return;
    }

    if (!confirm(`Möchten Sie ${selectedRoleIds.size} Rollen-Vorlage(n) wirklich löschen?`)) return;

    setDeletingRoles(true);
    for (const id of Array.from(selectedRoleIds)) {
      await fetch(`/api/admin/userrole-templates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${keycloak?.token}` }
      });
    }
    setSelectedRoleIds(new Set());
    await loadUserroleTemplates();
    setDeletingRoles(false);
    setUserroleMessage({ type: 'success', text: `${selectedRoleIds.size} Rollen-Vorlage(n) gelöscht` });
  };

  const allDeletableRolesSelected = currentRoles.filter(r => r.can_delete).length > 0 &&
    currentRoles.filter(r => r.can_delete).every(r => selectedRoleIds.has(r.id));

  // ==================== Channel Template Functions ====================

  const loadChannelTemplates = async () => {
    setLoadingChannels(true);
    try {
      const response = await fetch('/api/admin/channel-templates', {
        headers: {
          'Authorization': `Bearer ${keycloak?.token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setChannelTemplates(data);
      } else {
        setChannelMessage({ type: 'error', text: 'Fehler beim Laden der Kanal-Vorlagen' });
      }
    } catch (error) {
      setChannelMessage({ type: 'error', text: 'Netzwerkfehler' });
    } finally {
      setLoadingChannels(false);
    }
  };

  const openCreateChannelModal = () => {
    setEditingChannel(null);
    setChannelName('');
    setChannelDescription('');
    setChannelAutoCreate(true);
    setShowChannelModal(true);
  };

  const openEditChannelModal = (channel: ChannelTemplate) => {
    setEditingChannel(channel);
    setChannelName(channel.name);
    setChannelDescription(channel.description || '');
    setChannelAutoCreate(channel.auto_create);
    setShowChannelModal(true);
  };

  const closeChannelModal = () => {
    setShowChannelModal(false);
    setEditingChannel(null);
  };

  const saveChannel = async () => {
    setChannelMessage(null);
    try {
      if (editingChannel) {
        const updateResponse = await fetch(`/api/admin/channel-templates/${editingChannel.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${keycloak?.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: channelName,
            description: channelDescription || null,
            auto_create: channelAutoCreate,
          }),
        });

        if (!updateResponse.ok) {
          const error = await updateResponse.json();
          setChannelMessage({ type: 'error', text: error.detail || 'Fehler beim Aktualisieren' });
          return;
        }

        setChannelMessage({ type: 'success', text: 'Kanal-Vorlage aktualisiert' });
      } else {
        const createResponse = await fetch('/api/admin/channel-templates', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${keycloak?.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: channelName,
            description: channelDescription || null,
            auto_create: channelAutoCreate,
          }),
        });

        if (!createResponse.ok) {
          const error = await createResponse.json();
          setChannelMessage({ type: 'error', text: error.detail || 'Fehler beim Erstellen' });
          return;
        }

        setChannelMessage({ type: 'success', text: 'Kanal-Vorlage erstellt' });
      }

      closeChannelModal();
      loadChannelTemplates();
    } catch (error) {
      setChannelMessage({ type: 'error', text: 'Netzwerkfehler beim Speichern' });
    }
  };

  // Channel filter & pagination
  const filteredChannels = channelTemplates.filter((ch) => {
    const searchLower = channelSearchQuery.toLowerCase();
    return (
      ch.name.toLowerCase().includes(searchLower) ||
      (ch.description || '').toLowerCase().includes(searchLower)
    );
  });

  const totalChannels = filteredChannels.length;
  const totalChannelPages = Math.ceil(totalChannels / channelPageSize);
  const channelStartIndex = (channelCurrentPage - 1) * channelPageSize;
  const channelEndIndex = channelStartIndex + channelPageSize;
  const currentChannels = filteredChannels.slice(channelStartIndex, channelEndIndex);

  const handleChannelPageSizeChange = (newSize: number) => {
    setChannelPageSize(newSize);
    setChannelCurrentPage(1);
  };

  const handleChannelSearchChange = (value: string) => {
    setChannelSearchQuery(value);
    setChannelCurrentPage(1);
  };

  const handleSelectAllChannels = (checked: boolean) => {
    if (checked) {
      setSelectedChannelIds(new Set(currentChannels.map(c => c.id)));
    } else {
      setSelectedChannelIds(new Set());
    }
  };

  const handleSelectChannel = (channelId: string, checked: boolean) => {
    const newSet = new Set(selectedChannelIds);
    if (checked) {
      newSet.add(channelId);
    } else {
      newSet.delete(channelId);
    }
    setSelectedChannelIds(newSet);
  };

  const handleDeleteSelectedChannels = async () => {
    if (selectedChannelIds.size === 0) return;

    if (!confirm(`Möchten Sie ${selectedChannelIds.size} Kanal-Vorlage(n) wirklich löschen?`)) return;

    setDeletingChannels(true);
    for (const id of Array.from(selectedChannelIds)) {
      await fetch(`/api/admin/channel-templates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${keycloak?.token}` }
      });
    }
    setSelectedChannelIds(new Set());
    await loadChannelTemplates();
    setDeletingChannels(false);
    setChannelMessage({ type: 'success', text: `${selectedChannelIds.size} Kanal-Vorlage(n) gelöscht` });
  };

  const allChannelsSelected = currentChannels.length > 0 &&
    currentChannels.every(c => selectedChannelIds.has(c.id));

  // ==================== Channel Role Template Functions ====================

  const loadCrTemplates = async () => {
    setLoadingCr(true);
    try {
      const response = await fetch('/api/admin/channelrole-templates', {
        headers: {
          'Authorization': `Bearer ${keycloak?.token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCrTemplates(data);
      } else {
        setCrMessage({ type: 'error', text: 'Fehler beim Laden der Kanalrollen-Vorlagen' });
      }
    } catch (error) {
      setCrMessage({ type: 'error', text: 'Netzwerkfehler' });
    } finally {
      setLoadingCr(false);
    }
  };

  const openCreateCrModal = () => {
    setEditingCr(null);
    setCrName('');
    setCrDescription('');
    setCrAutoCreate(true);
    setCrRoleType('speak');
    setCrSpeechPriority('normal');
    setCrCanMute(false);
    setCrCanUnmute(false);
    setCrCanBan(false);
    setShowCrModal(true);
  };

  const openEditCrModal = (cr: ChannelRoleTemplate) => {
    setEditingCr(cr);
    setCrName(cr.name);
    setCrDescription(cr.description || '');
    setCrAutoCreate(cr.auto_create);
    setCrRoleType(cr.role_type as 'speak' | 'listen_only');
    setCrSpeechPriority(cr.speech_priority as 'normal' | 'high');
    setCrCanMute(cr.can_mute ?? false);
    setCrCanUnmute(cr.can_unmute ?? false);
    setCrCanBan(cr.can_ban ?? false);
    setShowCrModal(true);
  };

  const closeCrModal = () => {
    setShowCrModal(false);
    setEditingCr(null);
  };

  const saveCr = async () => {
    setCrMessage(null);
    try {
      if (editingCr) {
        const updateResponse = await fetch(`/api/admin/channelrole-templates/${editingCr.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${keycloak?.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: crName,
            description: crDescription || null,
            auto_create: crAutoCreate,
            role_type: crRoleType,
            speech_priority: crSpeechPriority,
            can_mute: crCanMute,
            can_unmute: crCanUnmute,
            can_ban: crCanBan,
          }),
        });

        if (!updateResponse.ok) {
          const error = await updateResponse.json();
          setCrMessage({ type: 'error', text: error.detail || 'Fehler beim Aktualisieren' });
          return;
        }

        setCrMessage({ type: 'success', text: 'Kanalrollen-Vorlage aktualisiert' });
      } else {
        const createResponse = await fetch('/api/admin/channelrole-templates', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${keycloak?.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: crName,
            description: crDescription || null,
            auto_create: crAutoCreate,
            role_type: crRoleType,
            speech_priority: crSpeechPriority,
            can_mute: crCanMute,
            can_unmute: crCanUnmute,
            can_ban: crCanBan,
          }),
        });

        if (!createResponse.ok) {
          const error = await createResponse.json();
          setCrMessage({ type: 'error', text: error.detail || 'Fehler beim Erstellen' });
          return;
        }

        setCrMessage({ type: 'success', text: 'Kanalrollen-Vorlage erstellt' });
      }

      closeCrModal();
      loadCrTemplates();
    } catch (error) {
      setCrMessage({ type: 'error', text: 'Netzwerkfehler beim Speichern' });
    }
  };

  // Channel role filter & pagination
  const filteredCr = crTemplates.filter((cr) => {
    const searchLower = crSearchQuery.toLowerCase();
    return (
      cr.name.toLowerCase().includes(searchLower) ||
      (cr.description || '').toLowerCase().includes(searchLower)
    );
  });

  const totalCr = filteredCr.length;
  const totalCrPages = Math.ceil(totalCr / crPageSize);
  const crStartIndex = (crCurrentPage - 1) * crPageSize;
  const crEndIndex = crStartIndex + crPageSize;
  const currentCr = filteredCr.slice(crStartIndex, crEndIndex);

  const handleCrPageSizeChange = (newSize: number) => {
    setCrPageSize(newSize);
    setCrCurrentPage(1);
  };

  const handleCrSearchChange = (value: string) => {
    setCrSearchQuery(value);
    setCrCurrentPage(1);
  };

  const handleSelectAllCr = (checked: boolean) => {
    if (checked) {
      setSelectedCrIds(new Set(currentCr.map(cr => cr.id)));
    } else {
      setSelectedCrIds(new Set());
    }
  };

  const handleSelectCr = (crId: string, checked: boolean) => {
    const newSet = new Set(selectedCrIds);
    if (checked) {
      newSet.add(crId);
    } else {
      newSet.delete(crId);
    }
    setSelectedCrIds(newSet);
  };

  const handleDeleteSelectedCr = async () => {
    if (selectedCrIds.size === 0) return;

    if (!confirm(`Möchten Sie ${selectedCrIds.size} Kanalrollen-Vorlage(n) wirklich löschen?`)) return;

    setDeletingCr(true);
    for (const id of Array.from(selectedCrIds)) {
      await fetch(`/api/admin/channelrole-templates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${keycloak?.token}` }
      });
    }
    setSelectedCrIds(new Set());
    await loadCrTemplates();
    setDeletingCr(false);
    setCrMessage({ type: 'success', text: `${selectedCrIds.size} Kanalrollen-Vorlage(n) gelöscht` });
  };

  const allCrSelected = currentCr.length > 0 &&
    currentCr.every(cr => selectedCrIds.has(cr.id));

  // ==================== Service Group Template Functions ====================

  const loadSgTemplates = async () => {
    setLoadingSg(true);
    try {
      const response = await fetch('/api/admin/servicegroup-templates', {
        headers: {
          'Authorization': `Bearer ${keycloak?.token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setSgTemplates(data);
      } else {
        setSgMessage({ type: 'error', text: 'Fehler beim Laden der Servicegruppen-Vorlagen' });
      }
    } catch (error) {
      setSgMessage({ type: 'error', text: 'Netzwerkfehler' });
    } finally {
      setLoadingSg(false);
    }
  };

  const openCreateSgModal = () => {
    setEditingSg(null);
    setSgName('');
    setSgDescription('');
    setSgAutoCreate(true);
    setShowSgModal(true);
  };

  const openEditSgModal = (sg: ServiceGroupTemplate) => {
    setEditingSg(sg);
    setSgName(sg.name);
    setSgDescription(sg.description || '');
    setSgAutoCreate(sg.auto_create);
    setShowSgModal(true);
  };

  const closeSgModal = () => {
    setShowSgModal(false);
    setEditingSg(null);
  };

  const saveSg = async () => {
    setSgMessage(null);
    try {
      if (editingSg) {
        const updateResponse = await fetch(`/api/admin/servicegroup-templates/${editingSg.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${keycloak?.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: sgName,
            description: sgDescription || null,
            auto_create: sgAutoCreate,
          }),
        });

        if (!updateResponse.ok) {
          const error = await updateResponse.json();
          setSgMessage({ type: 'error', text: error.detail || 'Fehler beim Aktualisieren' });
          return;
        }

        setSgMessage({ type: 'success', text: 'Servicegruppen-Vorlage aktualisiert' });
      } else {
        const createResponse = await fetch('/api/admin/servicegroup-templates', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${keycloak?.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: sgName,
            description: sgDescription || null,
            auto_create: sgAutoCreate,
          }),
        });

        if (!createResponse.ok) {
          const error = await createResponse.json();
          setSgMessage({ type: 'error', text: error.detail || 'Fehler beim Erstellen' });
          return;
        }

        setSgMessage({ type: 'success', text: 'Servicegruppen-Vorlage erstellt' });
      }

      closeSgModal();
      loadSgTemplates();
    } catch (error) {
      setSgMessage({ type: 'error', text: 'Netzwerkfehler beim Speichern' });
    }
  };

  // Service group filter & pagination
  const filteredSg = sgTemplates.filter((sg) => {
    const searchLower = sgSearchQuery.toLowerCase();
    return (
      sg.name.toLowerCase().includes(searchLower) ||
      (sg.description || '').toLowerCase().includes(searchLower)
    );
  });

  const totalSg = filteredSg.length;
  const totalSgPages = Math.ceil(totalSg / sgPageSize);
  const sgStartIndex = (sgCurrentPage - 1) * sgPageSize;
  const sgEndIndex = sgStartIndex + sgPageSize;
  const currentSg = filteredSg.slice(sgStartIndex, sgEndIndex);

  const handleSgPageSizeChange = (newSize: number) => {
    setSgPageSize(newSize);
    setSgCurrentPage(1);
  };

  const handleSgSearchChange = (value: string) => {
    setSgSearchQuery(value);
    setSgCurrentPage(1);
  };

  const handleSelectAllSg = (checked: boolean) => {
    if (checked) {
      setSelectedSgIds(new Set(currentSg.map(sg => sg.id)));
    } else {
      setSelectedSgIds(new Set());
    }
  };

  const handleSelectSg = (sgId: string, checked: boolean) => {
    const newSet = new Set(selectedSgIds);
    if (checked) {
      newSet.add(sgId);
    } else {
      newSet.delete(sgId);
    }
    setSelectedSgIds(newSet);
  };

  const handleDeleteSelectedSg = async () => {
    if (selectedSgIds.size === 0) return;

    if (!confirm(`Möchten Sie ${selectedSgIds.size} Servicegruppen-Vorlage(n) wirklich löschen?`)) return;

    setDeletingSg(true);
    for (const id of Array.from(selectedSgIds)) {
      await fetch(`/api/admin/servicegroup-templates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${keycloak?.token}` }
      });
    }
    setSelectedSgIds(new Set());
    await loadSgTemplates();
    setDeletingSg(false);
    setSgMessage({ type: 'success', text: `${selectedSgIds.size} Servicegruppen-Vorlage(n) gelöscht` });
  };

  const allSgSelected = currentSg.length > 0 &&
    currentSg.every(sg => selectedSgIds.has(sg.id));

  // ==================== Render ====================

  const tabClass = (tab: TabType) =>
    `flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
      activeTab === tab
        ? 'border-sayses-accent text-sayses-accent'
        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
    }`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Vorlagen</h1>
        <p className="text-slate-500 mt-1">Vorlagen für Tenants verwalten</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-4">
          <button className={tabClass('userroles')} onClick={() => setActiveTab('userroles')}>
            <Users size={18} />
            <span>Benutzer-Rollen</span>
          </button>
          <button className={tabClass('channels')} onClick={() => setActiveTab('channels')}>
            <Radio size={18} />
            <span>Kanäle</span>
          </button>
          <button className={tabClass('channelroles')} onClick={() => setActiveTab('channelroles')}>
            <Mic size={18} />
            <span>Kanalrollen</span>
          </button>
          <button className={tabClass('servicegroups')} onClick={() => setActiveTab('servicegroups')}>
            <Wrench size={18} />
            <span>Servicegruppen</span>
          </button>
        </nav>
      </div>

      {/* ==================== Userroles Tab ==================== */}
      {activeTab === 'userroles' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Toolbar */}
          <div className="p-4 border-b border-slate-200 flex items-center gap-4">
            <button
              onClick={openCreateRoleModal}
              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Rolle hinzufügen"
            >
              <Plus size={20} />
            </button>
            <button
              onClick={handleDeleteSelectedRoles}
              disabled={selectedRoleIds.size === 0 || deletingRoles}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Ausgewählte Rollen löschen"
            >
              <Minus size={20} />
            </button>
            <div className="flex-1 flex items-center gap-2">
              <div className="relative flex-1 max-w-md">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Suchen..."
                  value={roleSearchQuery}
                  onChange={(e) => handleRoleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
                />
              </div>
              <span className="text-sm text-slate-600">Alle auswählen</span>
              <input
                type="checkbox"
                checked={allDeletableRolesSelected}
                onChange={(e) => handleSelectAllRoles(e.target.checked)}
                className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
              />
            </div>
          </div>

          {/* Message */}
          {userroleMessage && (
            <div
              className={`mx-4 mt-4 flex items-center gap-2 p-4 rounded-lg ${
                userroleMessage.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {userroleMessage.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
              {userroleMessage.text}
            </div>
          )}

          {loadingUserroles ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-4 border-sayses-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-500">Lade Rollen-Vorlagen...</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="w-12 px-6 py-3"></th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Name</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Beschreibung</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Berechtigungen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentRoles.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                        Keine Rollen-Vorlagen vorhanden.
                      </td>
                    </tr>
                  ) : (
                    currentRoles.map((role) => (
                      <tr key={role.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          {role.can_delete ? (
                            <input
                              type="checkbox"
                              checked={selectedRoleIds.has(role.id)}
                              onChange={(e) => handleSelectRole(role.id, e.target.checked)}
                              className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
                            />
                          ) : (
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded" title="System-Rolle kann nicht gelöscht werden">
                              System
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-left">
                          <button
                            onClick={() => openEditRoleModal(role)}
                            className="text-sayses-accent hover:text-sayses-accent-hover text-left"
                          >
                            {role.name}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-slate-600">
                          {role.description || '-'}
                        </td>
                        <td className="px-6 py-4">
                          {role.permissions.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {role.permissions.map((perm) => (
                                <span
                                  key={perm.id}
                                  className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded"
                                >
                                  <Shield size={12} />
                                  {perm.description || perm.name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {totalRoles > 0 && (
                <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Einträge pro Seite:</span>
                    <select
                      value={rolePageSize}
                      onChange={(e) => handleRolePageSizeChange(Number(e.target.value))}
                      className="border border-slate-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-600">
                      {roleStartIndex + 1}-{Math.min(roleEndIndex, totalRoles)} von {totalRoles}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setRoleCurrentPage(p => Math.max(1, p - 1))}
                        disabled={roleCurrentPage === 1}
                        className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft size={20} className="text-slate-600" />
                      </button>
                      <button
                        onClick={() => setRoleCurrentPage(p => Math.min(totalRolePages, p + 1))}
                        disabled={roleCurrentPage === totalRolePages}
                        className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight size={20} className="text-slate-600" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ==================== Channels Tab ==================== */}
      {activeTab === 'channels' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Toolbar */}
          <div className="p-4 border-b border-slate-200 flex items-center gap-4">
            <button
              onClick={openCreateChannelModal}
              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Kanal hinzufügen"
            >
              <Plus size={20} />
            </button>
            <button
              onClick={handleDeleteSelectedChannels}
              disabled={selectedChannelIds.size === 0 || deletingChannels}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Ausgewählte Kanäle löschen"
            >
              <Minus size={20} />
            </button>
            <div className="flex-1 flex items-center gap-2">
              <div className="relative flex-1 max-w-md">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Suchen..."
                  value={channelSearchQuery}
                  onChange={(e) => handleChannelSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
                />
              </div>
              <span className="text-sm text-slate-600">Alle auswählen</span>
              <input
                type="checkbox"
                checked={allChannelsSelected}
                onChange={(e) => handleSelectAllChannels(e.target.checked)}
                className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
              />
            </div>
          </div>

          {/* Message */}
          {channelMessage && (
            <div
              className={`mx-4 mt-4 flex items-center gap-2 p-4 rounded-lg ${
                channelMessage.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {channelMessage.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
              {channelMessage.text}
            </div>
          )}

          {loadingChannels ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-4 border-sayses-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-500">Lade Kanal-Vorlagen...</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="w-12 px-6 py-3"></th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Name</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Beschreibung</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Auto-Erstellung</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentChannels.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                        Keine Kanal-Vorlagen vorhanden.
                      </td>
                    </tr>
                  ) : (
                    currentChannels.map((channel) => (
                      <tr key={channel.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedChannelIds.has(channel.id)}
                            onChange={(e) => handleSelectChannel(channel.id, e.target.checked)}
                            className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => openEditChannelModal(channel)}
                            className="text-sayses-accent hover:text-sayses-accent-hover"
                          >
                            {channel.name}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-slate-600">
                          {channel.description || '-'}
                        </td>
                        <td className="px-6 py-4">
                          {channel.auto_create ? (
                            <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">
                              <Check size={12} />
                              Ja
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">Nein</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {totalChannels > 0 && (
                <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Einträge pro Seite:</span>
                    <select
                      value={channelPageSize}
                      onChange={(e) => handleChannelPageSizeChange(Number(e.target.value))}
                      className="border border-slate-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-600">
                      {channelStartIndex + 1}-{Math.min(channelEndIndex, totalChannels)} von {totalChannels}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setChannelCurrentPage(p => Math.max(1, p - 1))}
                        disabled={channelCurrentPage === 1}
                        className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft size={20} className="text-slate-600" />
                      </button>
                      <button
                        onClick={() => setChannelCurrentPage(p => Math.min(totalChannelPages, p + 1))}
                        disabled={channelCurrentPage === totalChannelPages}
                        className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight size={20} className="text-slate-600" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ==================== Kanalrollen Tab ==================== */}
      {activeTab === 'channelroles' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Toolbar */}
          <div className="p-4 border-b border-slate-200 flex items-center gap-4">
            <button
              onClick={openCreateCrModal}
              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Kanalrolle hinzufügen"
            >
              <Plus size={20} />
            </button>
            <button
              onClick={handleDeleteSelectedCr}
              disabled={selectedCrIds.size === 0 || deletingCr}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Ausgewählte Kanalrollen löschen"
            >
              <Minus size={20} />
            </button>
            <div className="flex-1 flex items-center gap-2">
              <div className="relative flex-1 max-w-md">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Suchen..."
                  value={crSearchQuery}
                  onChange={(e) => handleCrSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
                />
              </div>
              <span className="text-sm text-slate-600">Alle auswählen</span>
              <input
                type="checkbox"
                checked={allCrSelected}
                onChange={(e) => handleSelectAllCr(e.target.checked)}
                className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
              />
            </div>
          </div>

          {/* Message */}
          {crMessage && (
            <div
              className={`mx-4 mt-4 flex items-center gap-2 p-4 rounded-lg ${
                crMessage.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {crMessage.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
              {crMessage.text}
            </div>
          )}

          {loadingCr ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-4 border-sayses-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-500">Lade Kanalrollen-Vorlagen...</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="w-12 px-6 py-3"></th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Name</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Beschreibung</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Auto-Erstellung</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentCr.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                        Keine Kanalrollen-Vorlagen vorhanden.
                      </td>
                    </tr>
                  ) : (
                    currentCr.map((cr) => (
                      <tr key={cr.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedCrIds.has(cr.id)}
                            onChange={(e) => handleSelectCr(cr.id, e.target.checked)}
                            className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => openEditCrModal(cr)}
                            className="text-sayses-accent hover:text-sayses-accent-hover"
                          >
                            {cr.name}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-slate-600">
                          {cr.description || '-'}
                        </td>
                        <td className="px-6 py-4">
                          {cr.auto_create ? (
                            <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">
                              <Check size={12} />
                              Ja
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">Nein</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {totalCr > 0 && (
                <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Einträge pro Seite:</span>
                    <select
                      value={crPageSize}
                      onChange={(e) => handleCrPageSizeChange(Number(e.target.value))}
                      className="border border-slate-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-600">
                      {crStartIndex + 1}-{Math.min(crEndIndex, totalCr)} von {totalCr}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCrCurrentPage(p => Math.max(1, p - 1))}
                        disabled={crCurrentPage === 1}
                        className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft size={20} className="text-slate-600" />
                      </button>
                      <button
                        onClick={() => setCrCurrentPage(p => Math.min(totalCrPages, p + 1))}
                        disabled={crCurrentPage === totalCrPages}
                        className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight size={20} className="text-slate-600" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ==================== Servicegruppen Tab ==================== */}
      {activeTab === 'servicegroups' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Toolbar */}
          <div className="p-4 border-b border-slate-200 flex items-center gap-4">
            <button
              onClick={openCreateSgModal}
              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Servicegruppe hinzufügen"
            >
              <Plus size={20} />
            </button>
            <button
              onClick={handleDeleteSelectedSg}
              disabled={selectedSgIds.size === 0 || deletingSg}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Ausgewählte Servicegruppen löschen"
            >
              <Minus size={20} />
            </button>
            <div className="flex-1 flex items-center gap-2">
              <div className="relative flex-1 max-w-md">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Suchen..."
                  value={sgSearchQuery}
                  onChange={(e) => handleSgSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
                />
              </div>
              <span className="text-sm text-slate-600">Alle auswählen</span>
              <input
                type="checkbox"
                checked={allSgSelected}
                onChange={(e) => handleSelectAllSg(e.target.checked)}
                className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
              />
            </div>
          </div>

          {/* Message */}
          {sgMessage && (
            <div
              className={`mx-4 mt-4 flex items-center gap-2 p-4 rounded-lg ${
                sgMessage.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {sgMessage.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
              {sgMessage.text}
            </div>
          )}

          {loadingSg ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-4 border-sayses-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-500">Lade Servicegruppen-Vorlagen...</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="w-12 px-6 py-3"></th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Name</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Beschreibung</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Auto-Erstellung</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentSg.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                        Keine Servicegruppen-Vorlagen vorhanden.
                      </td>
                    </tr>
                  ) : (
                    currentSg.map((sg) => (
                      <tr key={sg.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedSgIds.has(sg.id)}
                            onChange={(e) => handleSelectSg(sg.id, e.target.checked)}
                            className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => openEditSgModal(sg)}
                            className="text-sayses-accent hover:text-sayses-accent-hover"
                          >
                            {sg.name}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-slate-600">
                          {sg.description || '-'}
                        </td>
                        <td className="px-6 py-4">
                          {sg.auto_create ? (
                            <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">
                              <Check size={12} />
                              Ja
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">Nein</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {totalSg > 0 && (
                <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Einträge pro Seite:</span>
                    <select
                      value={sgPageSize}
                      onChange={(e) => handleSgPageSizeChange(Number(e.target.value))}
                      className="border border-slate-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-600">
                      {sgStartIndex + 1}-{Math.min(sgEndIndex, totalSg)} von {totalSg}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setSgCurrentPage(p => Math.max(1, p - 1))}
                        disabled={sgCurrentPage === 1}
                        className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft size={20} className="text-slate-600" />
                      </button>
                      <button
                        onClick={() => setSgCurrentPage(p => Math.min(totalSgPages, p + 1))}
                        disabled={sgCurrentPage === totalSgPages}
                        className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight size={20} className="text-slate-600" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Userrole Edit Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-bold text-slate-800">
                {editingRole ? 'Rolle bearbeiten' : 'Neue Rolle erstellen'}
              </h3>
              <button
                onClick={closeRoleModal}
                className="p-1 text-slate-500 hover:text-slate-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent"
                  placeholder="Rollenname..."
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Beschreibung
                </label>
                <input
                  type="text"
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent"
                  placeholder="Optionale Beschreibung..."
                />
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Berechtigungen
                </label>
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {allPermissions.map((perm) => (
                    <label
                      key={perm.id}
                      className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPermissions.has(perm.id)}
                        onChange={() => togglePermission(perm.id)}
                        className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
                      />
                      <div>
                        <span className="font-medium text-slate-700">{perm.name}</span>
                        {perm.description && (
                          <p className="text-sm text-slate-500">{perm.description}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={closeRoleModal}
                className="px-6 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={saveRole}
                disabled={!roleName.trim()}
                className="flex items-center gap-2 px-6 py-2 bg-sayses-accent hover:bg-sayses-accent-hover text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <Save size={18} />
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Channel Edit Modal */}
      {showChannelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-bold text-slate-800">
                {editingChannel ? 'Kanal bearbeiten' : 'Neuen Kanal erstellen'}
              </h3>
              <button
                onClick={closeChannelModal}
                className="p-1 text-slate-500 hover:text-slate-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent"
                  placeholder="Kanalname..."
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Beschreibung
                </label>
                <input
                  type="text"
                  value={channelDescription}
                  onChange={(e) => setChannelDescription(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent"
                  placeholder="Optionale Beschreibung..."
                />
              </div>

              {/* Auto Create */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={channelAutoCreate}
                    onChange={(e) => setChannelAutoCreate(e.target.checked)}
                    className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
                  />
                  <div>
                    <span className="font-medium text-slate-700">Kanal bei Neuanlage eines Tenants automatisch erstellen</span>
                    <p className="text-sm text-slate-500">Wenn aktiviert, wird dieser Kanal automatisch für jeden neuen Tenant angelegt.</p>
                  </div>
                </label>
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={closeChannelModal}
                className="px-6 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={saveChannel}
                disabled={!channelName.trim()}
                className="flex items-center gap-2 px-6 py-2 bg-sayses-accent hover:bg-sayses-accent-hover text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <Save size={18} />
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Channel Role Edit Modal */}
      {showCrModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-800">
                {editingCr ? 'Kanalrollen-Vorlage bearbeiten' : 'Neue Kanalrollen-Vorlage erstellen'}
              </h2>
              <button
                onClick={closeCrModal}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  type="text"
                  value={crName}
                  onChange={(e) => setCrName(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
                  placeholder="Name der Kanalrolle"
                  autoFocus
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Beschreibung</label>
                <input
                  type="text"
                  value={crDescription}
                  onChange={(e) => setCrDescription(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
                  placeholder="Optionale Beschreibung"
                />
              </div>

              {/* Typ */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Typ</label>
                <select
                  value={crRoleType}
                  onChange={(e) => setCrRoleType(e.target.value as 'speak' | 'listen_only')}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
                >
                  <option value="speak">Sprechen</option>
                  <option value="listen_only">Nur hören</option>
                </select>
              </div>

              {/* Sprachpriorität */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Sprachpriorität</label>
                <select
                  value={crSpeechPriority}
                  onChange={(e) => setCrSpeechPriority(e.target.value as 'normal' | 'high')}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
                >
                  <option value="normal">Normal</option>
                  <option value="high">Hoch</option>
                </select>
              </div>

              {/* Kanalrechte */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Kanalrechte</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={crCanMute}
                      onChange={(e) => setCrCanMute(e.target.checked)}
                      className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
                    />
                    <span className="text-sm text-slate-700">Stummschalten (Mute)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={crCanUnmute}
                      onChange={(e) => setCrCanUnmute(e.target.checked)}
                      className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
                    />
                    <span className="text-sm text-slate-700">Stummschaltung aufheben (Unmute)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={crCanBan}
                      onChange={(e) => setCrCanBan(e.target.checked)}
                      className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
                    />
                    <span className="text-sm text-slate-700">Aus Kanal entfernen (Ban)</span>
                  </label>
                </div>
              </div>

              {/* Auto Create */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={crAutoCreate}
                    onChange={(e) => setCrAutoCreate(e.target.checked)}
                    className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
                  />
                  <span className="text-sm text-slate-700">Auto-Erstellung bei neuem Tenant</span>
                </label>
              </div>

              <button
                onClick={saveCr}
                disabled={!crName.trim()}
                className="w-full bg-sayses-accent hover:bg-sayses-accent-hover disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service Group Edit Modal */}
      {showSgModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-bold text-slate-800">
                {editingSg ? 'Servicegruppe bearbeiten' : 'Neue Servicegruppe erstellen'}
              </h3>
              <button
                onClick={closeSgModal}
                className="p-1 text-slate-500 hover:text-slate-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={sgName}
                  onChange={(e) => setSgName(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent"
                  placeholder="Servicegruppenname..."
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Beschreibung
                </label>
                <input
                  type="text"
                  value={sgDescription}
                  onChange={(e) => setSgDescription(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent"
                  placeholder="Optionale Beschreibung..."
                />
              </div>

              {/* Auto Create */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sgAutoCreate}
                    onChange={(e) => setSgAutoCreate(e.target.checked)}
                    className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
                  />
                  <div>
                    <span className="font-medium text-slate-700">Servicegruppe bei Neuanlage eines Tenants automatisch erstellen</span>
                    <p className="text-sm text-slate-500">Wenn aktiviert, wird diese Servicegruppe automatisch für jeden neuen Tenant angelegt.</p>
                  </div>
                </label>
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={closeSgModal}
                className="px-6 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={saveSg}
                disabled={!sgName.trim()}
                className="flex items-center gap-2 px-6 py-2 bg-sayses-accent hover:bg-sayses-accent-hover text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <Save size={18} />
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
